import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Maximize2, X } from "lucide-react";
import { useRoom } from "./RoomProvider";
import { useAuth } from "../../lib/auth";
import { API } from "../../lib/api";

/**
 * Coworker — the AI, named by the dispatcher.
 *
 * Compact dock: an always-active widget bottom-left — live status, the
 * newest whisper, and a text box + mic that are ALWAYS there (no second
 * click to start talking to Nova). Hit "command center" to expand into
 * the full Nova Command Center (chat history, live steps, trust ladder,
 * gated autonomy, admin spotlight settings, action log) — one dedicated
 * surface for everything Nova, instead of it all being crammed into the
 * same small corner box the dock lives in.
 *
 * Every whisper here comes from a real event — Kaizen offer arrival, trip
 * state change, assignment outcome, or the Kaizen orphan-reconciliation
 * loop (see RoomProvider). No scripted lines, no mock array.
 *
 * Also runs live CPD settings on request (Bypass Intake, call forwarding,
 * dispatch strategy) — a VA types (or speaks) a plain request and this AI
 * executes it directly via /api/cpd/copilot/chat (real tool calls, not a
 * mock), then confirms what changed.
 */
export default function Coworker() {
  const { network, identity, allTrips = [], liveDrivers = [], realProviders = [], trustLadder = [], autonomyModes = {}, setAutonomyMode,
          followNova, setFollowNova, novaLiveEvents = [], spotlightEvent, replayEvent,
          spotlightIdleMs, setSpotlightIdleMs } = useRoom();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const latestWhisper = network.whispers[0] || null;

  const [commandCenterOpen, setCommandCenterOpen] = useState(false);
  const [ccTab, setCcTab] = useState("ops");
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [actionLog, setActionLog] = useState([]);
  const actionLogLoadedRef = useRef(false);
  const [spotlightSecInput, setSpotlightSecInput] = useState(null);
  const [spotlightSaving, setSpotlightSaving] = useState(false);
  const msgsRef = useRef(null);
  const historyLoadedRef = useRef(false);
  const idSeq = useRef(0);
  const nextId = () => `m${++idSeq.current}`;

  const name = identity?.coworker_name || "Nova";

  const saveSpotlightSeconds = async () => {
    const secs = Number(spotlightSecInput);
    if (!secs || secs < 1 || secs > 60) return;
    setSpotlightSaving(true);
    try {
      await setSpotlightIdleMs(Math.round(secs * 1000));
    } finally {
      setSpotlightSaving(false);
    }
  };

  // L4 · Nova alive — live stats derived from real state. A trip stuck in a
  // non-terminal status (ACCEPTED/EN_ROUTE/etc.) for a long time without any
  // update is a zombie record, not a genuinely live trip — NEMT/Kaizen rides
  // are same-day by nature, so anything not updated in 48h is excluded here
  // (the rest of the Floor's lanes already apply their own stricter relevance
  // filters; this dock's raw status-only count previously had none, which is
  // why it could show trips no other panel displayed).
  const STALE_MS = 48 * 60 * 60 * 1000;
  const stats = React.useMemo(() => {
    const now = Date.now();
    const isFresh = (t) => {
      const ts = t.updated_at || t.created_at;
      if (!ts) return false;
      const when = new Date(ts).getTime();
      return Number.isFinite(when) && (now - when) < STALE_MS;
    };
    const active = allTrips.filter(t =>
      isFresh(t) && ["ACCEPTED","EN_ROUTE","ARRIVED_PU","ON_TRIP","ARRIVED_DO"].includes(String(t.status).toUpperCase())
    ).length;
    const pending = allTrips.filter(t =>
      isFresh(t) && ["REQUESTED","TP_ACCEPTED","ASSIGNED"].includes(String(t.status).toUpperCase())
    ).length;
    const drivers = new Set(allTrips.filter(isFresh).map(t => t.assigned_driver?.driver_id).filter(Boolean)).size;
    return { active, pending, drivers };
  }, [allTrips]);

  // Chat history loads once, regardless of whether the compact dock or the
  // command center is what first sends a message — same transcript either way.
  useEffect(() => {
    if (historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    const token = localStorage.getItem("astat_admin_token");
    fetch(`${API}/cpd/copilot/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setMessages(Array.isArray(d?.messages) ? d.messages.map(x => ({ ...x, id: nextId() })) : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, streaming, commandCenterOpen]);

  const openActionLog = () => {
    if (actionLogLoadedRef.current) return;
    actionLogLoadedRef.current = true;
    const token = localStorage.getItem("astat_admin_token");
    fetch(`${API}/cpd/copilot/actions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setActionLog(Array.isArray(d?.actions) ? d.actions : []))
      .catch(() => {});
  };

  const sendChat = async (e, overrideText) => {
    e.preventDefault();
    const text = (overrideText ?? chatInput).trim();
    if (!text || streaming) return;
    setChatInput("");
    const assistantId = nextId();
    setMessages(m => [...m, { id: nextId(), role: "user", content: text }, { id: assistantId, role: "assistant", content: "" }]);
    setStreaming(true);
    // Safety net: this fetch previously had no timeout at all, so a hung
    // backend response (slow tool call, dropped connection that doesn't
    // surface as a network error) could leave `streaming` true forever —
    // disabling the input with no way to recover short of a page reload.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const token = localStorage.getItem("astat_admin_token");
      const res = await fetch(`${API}/cpd/copilot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, coworker_name: name }),
        signal: controller.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.type === "delta") {
            setMessages(m => m.map(msg => msg.id === assistantId ? { ...msg, content: msg.content + evt.content } : msg));
          } else if (evt.type === "tool_start") {
            // Visible preview-before-execute trail: every tool call gets its
            // own inline chip in the transcript, not just the final sentence.
            setMessages(m => {
              const idx = m.findIndex(x => x.id === assistantId);
              const toolMsg = { id: nextId(), role: "tool", name: evt.name, status: "running" };
              if (idx === -1) return [...m, toolMsg];
              const next = [...m];
              next.splice(idx, 0, toolMsg);
              return next;
            });
          } else if (evt.type === "tool_result") {
            setMessages(m => {
              let matched = false;
              return m.map(msg => {
                if (!matched && msg.role === "tool" && msg.name === evt.name && msg.status === "running") {
                  matched = true;
                  return { ...msg, status: "done", result: evt.result };
                }
                return msg;
              });
            });
          } else if (evt.type === "truthfulness_check") {
            setMessages(m => m.map(msg => msg.id === assistantId
              ? { ...msg, truthCheck: { ok: evt.ok, mismatches: evt.mismatches || [], checked: evt.checked_count || 0 } }
              : msg));
          } else if (evt.type === "error") {
            setMessages(m => m.map(msg => msg.id === assistantId ? { ...msg, content: `couldn't finish that — ${evt.message}` } : msg));
          }
        }
      }
    } catch {
      setMessages(m => m.map(msg => msg.id === assistantId ? { ...msg, content: "lost connection mid-response — try again" } : msg));
    } finally {
      clearTimeout(timeoutId);
      setStreaming(false);
    }
  };

  // Voice input — a VA speaks instead of typing. Records with the browser
  // mic, uploads to Whisper for transcription, then sends the transcribed
  // sentence through the exact same sendChat path a typed message takes —
  // so every tool-call confirmation rule still applies to a spoken request.
  const toggleMic = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (blob.size < 800) return; // essentially silent — nothing worth transcribing
        setTranscribing(true);
        try {
          const token = localStorage.getItem("astat_admin_token");
          const form = new FormData();
          form.append("audio", blob, "recording.webm");
          const res = await fetch(`${API}/cpd/copilot/transcribe`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          const data = await res.json();
          const text = (data?.text || "").trim();
          if (text) {
            setChatInput(text);
            sendChat({ preventDefault: () => {} }, text);
          }
        } catch {
          // transcription unavailable — VA can still type
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.onerror = () => {
        // Gap that used to leave `recording` stuck true forever with no
        // recovery path if the recorder itself failed instead of stopping
        // cleanly (onstop never fires on an error).
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(false);
      };
      recorder.start();
      setRecording(true);
    } catch {
      // mic permission denied/unavailable — text input still works
    }
  };

  const ChatForm = ({ testPrefix }) => (
    <form className="cw-chat-form" onSubmit={sendChat}>
      <input
        className="cw-chat-input"
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        placeholder={recording ? "listening…" : transcribing ? "transcribing…" : `tell ${name.toLowerCase()} what to do…`}
        disabled={streaming}
        data-testid={`${testPrefix}-input`}
      />
      <button
        type="button"
        className={`cw-chat-mic ${recording ? "cw-chat-mic--recording" : ""}`}
        onClick={toggleMic}
        disabled={streaming || transcribing}
        title={recording ? "stop and send" : "speak to " + name}
        data-testid={`${testPrefix}-mic`}
      >
        {recording ? <Square size={14} /> : <Mic size={14} />}
      </button>
      <button type="submit" className="cw-chat-send" disabled={streaming || !chatInput.trim()} data-testid={`${testPrefix}-send`}>
        {streaming || transcribing ? "…" : "→"}
      </button>
    </form>
  );

  const ChatTranscript = () => (
    <div className="cw-chat-msgs nova-cc-msgs" ref={msgsRef} data-testid="coworker-chat-panel">
      {messages.length === 0 && (
        <div className="cw-chat-empty">
          tell me what to do — e.g. "turn on CPD bypass" or "give me a floor report"
        </div>
      )}
      {messages.map((m, i) => (
        m.role === "tool" ? (
          <div key={m.id || i} className={`cw-tool-chip cw-tool-chip--${m.status}`} data-testid={`coworker-chat-tool-${i}`}>
            <span className="cw-tool-dot" />
            {toolLabel(m.name)} {m.status === "running" ? "…" : `· ${toolResultSummary(m.name, m.result)}`}
          </div>
        ) : (
          <div key={m.id || i} className={`cw-chat-msg ${m.role}`} data-testid={`coworker-chat-msg-${i}`}>
            {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            {m.role === "assistant" && m.truthCheck && (
              <div className={`cw-truth-badge ${m.truthCheck.ok ? "cw-truth-badge--ok" : "cw-truth-badge--warn"}`}
                   data-testid={`coworker-truth-badge-${i}`}
                   title={m.truthCheck.ok ? "Every figure Nova said matched the live data it looked up." : `Unverified: ${m.truthCheck.mismatches.join(", ")}`}>
                {m.truthCheck.ok ? `✓ verified against live data` : `⚠ ${m.truthCheck.mismatches.length} figure(s) unverified`}
              </div>
            )}
          </div>
        )
      ))}
    </div>
  );

  return (
    <>
      <motion.div
        className={`coworker ${spotlightEvent ? "coworker--nova-active" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.9 }}
        data-testid="coworker-compact-dock"
      >
        <div className="cw-header">
          <span className="cw-glow" />
          <span className="cw-name" data-testid="coworker-name">{name}</span>
          <span className="cw-status">
            watching · {stats.active} live · {stats.pending} pending · {stats.drivers} drv
          </span>
          <button type="button" className={`cw-chat-toggle cw-follow-toggle ${followNova ? "cw-follow-toggle--on" : ""}`}
                  onClick={() => setFollowNova(!followNova)} data-testid="coworker-follow-nova-toggle"
                  title="When on, your screen opens the exact card Nova is working on, live — off by default, never forced.">
            {followNova ? "following ●" : "follow nova"}
          </button>
          <button type="button" className="cw-expand-btn" onClick={() => setCommandCenterOpen(true)} data-testid="coworker-expand-btn"
                  title={`Open ${name}'s command center — chat history, live steps, trust ladder, autonomy & settings, action log`}>
            <Maximize2 size={12} /> command center
          </button>
        </div>

        {latestWhisper && (
          <div className="cw-whispers" data-testid="coworker-latest-whisper">
            <motion.div
              key={latestWhisper.id}
              className={`cw-whisper ${latestWhisper.actionable ? "actionable" : ""} ${latestWhisper.tone === "warn" ? "cw-whisper--warn" : ""}`}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
              data-testid="coworker-nova-scan-line"
            >
              <div className="cw-w-text">{latestWhisper.text}</div>
              {latestWhisper.confidence !== null && (
                <div className="cw-w-meta">
                  <span className="cw-conf">
                    {latestWhisper.confidence >= 0.85 ? "confident" : latestWhisper.confidence >= 0.7 ? "likely" : "unsure"}
                    {" · "}
                    {Math.round(latestWhisper.confidence * 100)}%
                  </span>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {ChatForm({ testPrefix: "coworker-compact" })}
      </motion.div>

      <AnimatePresence>
        {commandCenterOpen && (
          <motion.div
            className="nova-cc-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCommandCenterOpen(false)}
            data-testid="nova-command-center-backdrop"
          >
            <motion.div
              className="nova-cc"
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="nova-command-center-modal"
            >
              <div className="nova-cc-head">
                <span className="cw-glow" />
                <span className="nova-cc-title">{name} · command center</span>
                <button type="button" className="nova-cc-close" onClick={() => setCommandCenterOpen(false)} data-testid="nova-command-center-close">
                  <X size={16} />
                </button>
              </div>
              <div className="nova-cc-body">
                <div className="nova-cc-col nova-cc-col--chat">
                  {ChatTranscript()}
                  {ChatForm({ testPrefix: "coworker-chat" })}
                </div>
                <div className="nova-cc-col nova-cc-col--ops">
                  <div className="nova-cc-subtabs">
                    <button type="button" className={ccTab === "ops" ? "active" : ""} onClick={() => setCcTab("ops")} data-testid="nova-cc-tab-ops">
                      overview & autonomy
                    </button>
                    <button type="button" className={ccTab === "log" ? "active" : ""} onClick={() => { setCcTab("log"); openActionLog(); }} data-testid="nova-cc-tab-log">
                      action log
                    </button>
                  </div>

                  {ccTab === "ops" && (
                    <div className="nova-cc-scroll">
                      <div className="cw-jd-title">{"what i'm watching"}</div>
                      <div className="cw-jd-grid">
                        <span>{allTrips.length} trips</span>
                        <span>{liveDrivers.length} drivers online</span>
                        <span>{realProviders.length} providers</span>
                        <span>ETAs · credentials · weather · SLAs</span>
                      </div>

                      {followNova && (
                        <div className="cw-live-steps" data-testid="coworker-live-steps">
                          <div className="cw-live-steps-title">nova · live steps</div>
                          {novaLiveEvents.length === 0 && (
                            <div className="cw-chat-empty">watching — nothing autonomous has happened yet</div>
                          )}
                          <AnimatePresence>
                            {novaLiveEvents.slice(0, 6).map((e, i) => (
                              <motion.button
                                key={e.id}
                                type="button"
                                className={`cw-live-step cw-live-step--${e.phase} ${spotlightEvent?.id === e.id ? "cw-live-step--active" : ""}`}
                                onClick={() => replayEvent(e)}
                                data-testid={`coworker-live-step-${i}`}
                                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1 - i * 0.12, x: 0 }}
                                exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.4 }}
                              >
                                <span className="cw-live-step-dot" />
                                <span className="cw-live-step-text">nova: {e.label}</span>
                                <span className="cw-live-step-time">{relTime(e.ts)}</span>
                              </motion.button>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}

                      {trustLadder.length > 0 && (
                        <div className="cw-jd-trust" data-testid="coworker-trust-ladder">
                          <div className="cw-jd-trust-title">trust ladder</div>
                          {trustLadder.slice(0, 3).map(r => (
                            <div key={r.action_type} className="cw-jd-trust-row" data-testid={`coworker-trust-${r.action_type}`}>
                              <span>{r.action_type.replace(/_/g, " ")}</span>
                              <span className={`cw-jd-trust-pct cw-jd-trust-pct--${r.level}`}>
                                {Math.round(r.trust_pct * 100)}% ({r.total})
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="cw-jd-autonomy" data-testid="coworker-autonomy-panel">
                        <div className="cw-jd-trust-title">gated autonomy — training wheels</div>
                        {Object.entries(autonomyModes).map(([actionType, info]) => (
                          <div key={actionType} className="cw-jd-autonomy-row" data-testid={`coworker-autonomy-${actionType}`}>
                            <span>{actionType.replace(/_/g, " ")}</span>
                            <select
                              value={info.mode}
                              onChange={(e) => setAutonomyMode(actionType, e.target.value)}
                              className="cw-jd-autonomy-select"
                              data-testid={`coworker-autonomy-select-${actionType}`}
                            >
                              <option value="manual">manual — always ask</option>
                              <option value="training_wheels">training wheels — auto + undo</option>
                              <option value="autonomous">autonomous — auto, quiet</option>
                            </select>
                          </div>
                        ))}
                      </div>

                      {isAdmin && (
                        <div className="cw-jd-autonomy" data-testid="coworker-spotlight-settings">
                          <div className="cw-jd-trust-title">follow nova — spotlight idle (sec)</div>
                          <div className="cw-jd-autonomy-row">
                            <input
                              type="number"
                              min={1}
                              max={60}
                              placeholder={String(Math.round((spotlightIdleMs || 9000) / 1000))}
                              value={spotlightSecInput ?? ""}
                              onChange={(e) => setSpotlightSecInput(e.target.value)}
                              className="cw-jd-autonomy-select"
                              style={{ width: "64px" }}
                              data-testid="coworker-spotlight-idle-input"
                            />
                            <button
                              onClick={saveSpotlightSeconds}
                              disabled={spotlightSaving || !spotlightSecInput}
                              data-testid="coworker-spotlight-idle-save"
                              className="cw-jd-autonomy-select"
                              style={{ cursor: "pointer" }}
                            >
                              {spotlightSaving ? "saving…" : "save"}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="cw-jd-note">{"i'll speak up when something needs you. — "}{name.toLowerCase()}</div>
                    </div>
                  )}

                  {ccTab === "log" && (
                    <div className="nova-cc-scroll" data-testid="coworker-log-panel">
                      {actionLog.length === 0 && <div className="cw-chat-empty">no actions executed yet</div>}
                      {actionLog.map((a, i) => (
                        <div key={a.id || i} className="cw-log-row" data-testid={`coworker-log-row-${i}`}>
                          <div className="cw-log-tool">{a.tool}</div>
                          <div className="cw-log-meta">{a.actor_name || a.actor_email} · {relTime(a.at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function toolLabel(name) {
  const labels = {
    get_bypass_status: "checking bypass status",
    set_bypass_enabled: "toggling bypass intake",
    set_bypass_forward: "setting call forwarding",
    clear_bypass_forward: "clearing call forwarding",
    get_active_bypass_calls: "checking active bypass calls",
    get_dispatch_strategy: "checking dispatch strategy",
    set_dispatch_strategy: "changing dispatch strategy",
    get_floor_report: "pulling a floor report",
    bulk_archive_completed_trips: "checking trips to archive",
    cleanup_stuck_cpd_intakes: "checking stuck CPD intakes",
  };
  return labels[name] || (name || "").replace(/_/g, " ");
}

function toolResultSummary(name, result) {
  if (!result) return "done";
  if (result.error) return result.message || result.error;
  if (result.preview) return result.message || "preview ready";
  if (name === "bulk_archive_completed_trips" && result.archived_count != null) return `archived ${result.archived_count} trip(s)`;
  if (name === "cleanup_stuck_cpd_intakes" && result.cancelled_count != null) return `cancelled ${result.cancelled_count} intake(s)`;
  if (result.ok) return "done";
  return "checked";
}

function relTime(iso) {
  if (!iso) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}
